import { offset, shift } from "@floating-ui/react";
import * as yup from "yup";
import {
  MdOutlineBackpack,
  MdOutlineBed,
  MdOutlineChildFriendly,
  MdOutlineVilla,
  MdApartment,
  MdMuseum,
  MdNightlife,
  MdVolunteerActivism,
  MdOutlinePhotoCameraBack,
  MdFestival,
  MdFamilyRestroom,
  MdOutlineGroups,
} from "react-icons/md";
import { PiParachuteBold, PiWineFill } from "react-icons/pi";
import { BiBuildings, BiBuildingHouse, BiSolidBriefcase } from "react-icons/bi";
import {
  FaUmbrellaBeach,
  FaMountain,
  FaMosque,
  FaLaptop,
} from "react-icons/fa";
import { GiAncientRuins, GiPalette } from "react-icons/gi";
import { FaCartShopping, FaPersonWalkingLuggage } from "react-icons/fa6";
import { TbBus, TbMapHeart, TbMapCheck } from "react-icons/tb";
import MapboxSearch from "@/components/formComponents/MapboxSearch/MapboxSearch";
import RadioButtons from "@/components/formComponents/RadioButtons/RadioButtons";
import RadioButtonIconLabel from "@/components/formComponents/RadioButtons/RadioButtonIconLabel";
import SubmitButton from "../SubmitButton";
import DateComponent from "@/components/formComponents/DateComponent/DateComponent";
import PlacesSearch from "@/components/formComponents/PlacesSearch/PlacesSearch";

export function GuideFormPageData() {
  const page1 = {
    title: "Where do you want to go?",
    inputs: [
      {
        type: "search",
        component: PlacesSearch,
        controlled: true,
        containerStyles: { marginTop: "-10vh" },
        props: {
          searchType: "country",
          id: "location",
        },
        label: "Choose a destination: ",
        instructions: `Try to be specific! "Bali, Indonesia" will get better results than "Indonesia"`,
        placeholder: "Search for destination",
      },
    ],
  };

  const page2 = {
    title: "What dates are you traveling?",
    inputs: [
      {
        type: "date",
        label: "Dates (Optional):",
        component: DateComponent,
        containerStyles: { marginTop: "-10vh" },
        props: {
          id: "trip_length",
          mode: "range",
          min: 2,
          max: 14,
          disabled: [{ before: new Date() }],
          floatingUiProps: {
            middleware: [offset({ crossAxis: -30, mainAxis: -60 }), shift()],
          },
        },
      },
    ],
  };

  const page3 = {
    title: "What is your travel style?",
    inputs: [
      {
        type: "radio",
        label: "Choose one:",
        component: RadioButtons,
        controlled: true,
        props: {
          id: "travel_style",
          customLabels: true,
          direction: "horizontal",
          options: [
            {
              label: (
                <RadioButtonIconLabel
                  text="Business"
                  icon={
                    <BiSolidBriefcase size={30} color="var(--light-icon)" />
                  }
                />
              ),
              value: "business",
            },
            {
              label: (
                <RadioButtonIconLabel
                  text="Digital Nomad"
                  icon={<FaLaptop size={30} color="var(--light-icon)" />}
                />
              ),
              value: "digital-nomad",
            },
            {
              label: (
                <RadioButtonIconLabel
                  text="Family-friendly"
                  icon={
                    <MdFamilyRestroom size={30} color="var(--light-icon)" />
                  }
                />
              ),
              value: "family-friendly",
            },
            {
              label: (
                <RadioButtonIconLabel
                  text="Romantic Getaway"
                  icon={<TbMapHeart size={30} color="var(--light-icon)" />}
                />
              ),
              value: "romantic-getaway",
            },
            {
              label: (
                <RadioButtonIconLabel
                  text="Senior"
                  icon={<TbMapCheck size={30} color="var(--light-icon)" />}
                />
              ),
              value: "senior",
            },
            {
              label: (
                <RadioButtonIconLabel
                  text="Solo"
                  icon={
                    <FaPersonWalkingLuggage
                      size={30}
                      color="var(--light-icon)"
                    />
                  }
                />
              ),
              value: "solo",
            },
            {
              label: (
                <RadioButtonIconLabel
                  text="Travelling with Friends"
                  icon={<MdOutlineGroups size={30} color="var(--light-icon)" />}
                />
              ),
              value: "travelling-with-friends",
            },
          ],
        },
      },
    ],
  };

  const page4 = {
    title: "What is your budget?",
    inputs: [
      {
        type: "radio",
        label: "Choose one:",
        component: RadioButtons,
        controlled: true,
        props: {
          id: "budget",
          customLabels: true,
          direction: "horizontal",
          options: [
            {
              label: (
                <RadioButtonIconLabel text="$" fontSize="var(--font-size-md)" />
              ),
              value: "low_budget",
            },
            {
              label: (
                <RadioButtonIconLabel
                  text="$$"
                  fontSize="var(--font-size-md)"
                />
              ),
              value: "average_budget",
            },
            {
              label: (
                <RadioButtonIconLabel
                  text={"$$$"}
                  fontSize="var(--font-size-md)"
                />
              ),
              value: "high_budget",
            },
            {
              label: (
                <RadioButtonIconLabel
                  text={"$$$$"}
                  fontSize="var(--font-size-md)"
                />
              ),
              value: "luxury_budget",
            },
          ],
        },
      },
    ],
  };

  const page5 = {
    title: "What kind of accommodation do you prefer?",
    inputs: [
      {
        type: "radio",
        label: "Choose all that apply (Optional):",
        component: RadioButtons,
        controlled: true,
        props: {
          id: "accommodation",
          customLabels: true,
          direction: "horizontal",
          inputType: "checkbox",
          options: [
            {
              label: (
                <RadioButtonIconLabel
                  text="Hotel"
                  icon={<BiBuildings size={30} color="var(--light-icon)" />}
                />
              ),
              value: "hotel",
            },
            {
              label: (
                <RadioButtonIconLabel
                  text="Hostel"
                  icon={<MdOutlineBed size={30} color="var(--light-icon)" />}
                />
              ),
              value: "hostel",
            },
            {
              label: (
                <RadioButtonIconLabel
                  text="Apartment / House"
                  icon={<MdApartment size={30} color="var(--light-icon)" />}
                />
              ),
              value: "apartment-or-house",
            },
            {
              label: (
                <RadioButtonIconLabel
                  text="Villa"
                  icon={<MdOutlineVilla size={30} color="var(--light-icon)" />}
                />
              ),
              value: "villa",
            },
            {
              label: (
                <RadioButtonIconLabel
                  text="Resort"
                  icon={<FaUmbrellaBeach size={30} color="var(--light-icon)" />}
                />
              ),
              value: "resort",
            },
            {
              label: (
                <RadioButtonIconLabel
                  text="Homestay"
                  icon={<BiBuildingHouse size={30} color="var(--light-icon)" />}
                />
              ),
              value: "homestay",
            },
          ],
        },
      },
    ],
  };

  const page6 = {
    title: "What kind of activities do you prefer?",
    inputs: [
      {
        type: "radio",
        label: "Choose all that apply (Optional):",
        component: RadioButtons,
        controlled: true,
        props: {
          id: "activities",
          customLabels: true,
          direction: "horizontal",
          inputType: "checkbox",
          options: [
            {
              label: (
                <RadioButtonIconLabel
                  text="Arts / Culture"
                  icon={<GiPalette size={30} color="var(--light-icon)" />}
                />
              ),
              value: "arts-and-culture",
            },

            {
              label: (
                <RadioButtonIconLabel
                  text="Family Activities"
                  icon={
                    <MdFamilyRestroom size={30} color="var(--light-icon)" />
                  }
                />
              ),
              value: "family-activities",
            },
            {
              label: (
                <RadioButtonIconLabel
                  text="Festivals / Events"
                  icon={<MdFestival size={30} color="var(--light-icon)" />}
                />
              ),
              value: "festivals-and-events",
            },
            {
              label: (
                <RadioButtonIconLabel
                  text="Gastronomy"
                  icon={<PiWineFill size={30} color="var(--light-icon)" />}
                />
              ),
              value: "gastronomy",
            },
            {
              label: (
                <RadioButtonIconLabel
                  text="Nightlife / Entertainment"
                  icon={<MdNightlife size={30} color="var(--light-icon)" />}
                />
              ),
              value: "nightlife-and-entertainment",
            },
            {
              label: (
                <RadioButtonIconLabel
                  text="Outdoor Adventures"
                  icon={<PiParachuteBold size={30} color="var(--light-icon)" />}
                />
              ),
              value: "outdoor-adventures",
            },

            {
              label: (
                <RadioButtonIconLabel
                  text="Sightseeing / tours"
                  icon={<GiAncientRuins size={30} color="var(--light-icon)" />}
                />
              ),
              value: "sightseeing-and-tours",
            },

            {
              label: (
                <RadioButtonIconLabel
                  text="Volunteering"
                  icon={
                    <MdVolunteerActivism size={30} color="var(--light-icon)" />
                  }
                />
              ),
              value: "volunteering-and-social-impact",
            },
          ],
        },
      },
    ],
  };

  const page7 = {
    title: "Almost finished!",
    inputs: [
      {
        type: "radio",
        label: "Would you like to have your name displayed on your guide?",
        component: RadioButtons,
        controlled: true,
        props: {
          id: "display_name",
          customLabels: false,
          direction: "vertical",
          inputType: "checkbox",
          options: [
            {
              label: "I want my name displayed publically on my travel guide",
              value: "display-name-yes",
            },
            { label: "Disclaimer will go here", value: "terms-agree-yes" },
          ],
        },
      },
      {
        type: "submit",
        component: SubmitButton,
      },
    ],
  };

  const pages = [page1, page2, page3, page4, page5, page6];

  return pages;
}

export const images = [
  "https://res.cloudinary.com/dail4fbms/image/upload/v1699453917/Form0_lush_forest_9fda77fc02.jpg",
  "https://res.cloudinary.com/dail4fbms/image/upload/v1699453915/Form1_outback1_ae5cef6976.jpg",
  "https://res.cloudinary.com/dail4fbms/image/upload/v1699453919/Form2_snow1_75a3b383f1.jpg",
  "https://res.cloudinary.com/dail4fbms/image/upload/v1699453919/Form4_lake3_444741b415.jpg",
  "https://res.cloudinary.com/dail4fbms/image/upload/v1699453925/Form5_bali3_b3cbeeffc9.jpg",
  "https://res.cloudinary.com/dail4fbms/image/upload/v1699453921/Form6_pink_lake2_937ac95e7b.jpg",
  "https://res.cloudinary.com/dail4fbms/image/upload/v1699453919/Form7_snow3_af18e8d793.jpg",
];
